import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Loader2 } from "lucide-react";
import { useApi } from "../hooks/useApi";

/**
 * The identity estate as a graph: identity → privilege → device → OAuth/SaaS app → data reach →
 * business service.
 *
 * <p>It replaces the READING, not the evidence. The drawer's nine text sections each stated a true
 * fact and left the reader to notice that a directory role and a `Mail.Send` grant on the same
 * account are one finding. The chain draws that join.</p>
 *
 * <p><b>Three encoding channels, one job each.</b> The glyph says WHAT a node is, the ring colour
 * how bad it is, the radius how much exposure it carries. Colour cannot carry type as well: a node
 * dragged away from its cluster would stop being identifiable, and dragging is the point.</p>
 *
 * <p><b>What it refuses to draw.</b> A stage nobody could read is dashed and grey with the reason —
 * an unread stage must never look like a safe one. Business service stays an admitted gap until
 * something in the data maps an application to a service owner.</p>
 */

// Stage bands, left to right. The order is the chain; it is fixed, not derived from the payload.
export const STAGES = [
  { key: "Identity", label: "Identity" },
  { key: "Privilege", label: "Privilege" },
  { key: "Device", label: "Device" },
  { key: "App", label: "Application" },
  { key: "Data", label: "Data reach" },
  { key: "Service", label: "Business service" },
];

const STAGE_INDEX = STAGES.reduce((acc, s, i) => ({ ...acc, [s.key]: i }), {});

// Hard-coded like AiIntelligenceGraph's kind dots: these read on both themes, while the panels
// around them use theme tokens and follow light/dark with the rest of the dashboard.
const SEVERITY = {
  red: { ring: "#B4453E", fill: "#FFF7F6", label: "Critical / high" },
  amber: { ring: "#C28A2B", fill: "#FFFBF2", label: "Needs a look" },
  teal: { ring: "#2F7D6E", fill: "#F4FBF8", label: "Observed, no problem found" },
  grey: { ring: "#9A9AA2", fill: "#FAFAF8", label: "Not observed / not mapped" },
};

const tone = (severity) => SEVERITY[severity] || SEVERITY.grey;

/**
 * Type glyphs — the third channel. Each entry is a list of [svgTag, attrs] drawn in a 24×24 box
 * centred on the origin, so one spec scales to any node radius.
 */
const GLYPHS = {
  Identity: [
    ["circle", { cx: 0, cy: -3, r: 2.6 }],
    ["path", { d: "M -4.6 4.8 a 4.6 4.2 0 0 1 9.2 0" }],
  ],
  Privilege: [
    ["circle", { cx: -3.4, cy: 0, r: 2.4 }],
    ["path", { d: "M -1 0 H 5.6 M 3.4 0 V 2.2 M 5.2 0 V 1.8" }],
  ],
  Device: [
    ["rect", { x: -6, y: -5.2, width: 12, height: 8.4, rx: 1 }],
    ["path", { d: "M -3 5.8 H 3 M 0 3.2 V 5.8" }],
  ],
  App: [
    ["rect", { x: -5.4, y: -5.4, width: 4.6, height: 4.6, rx: 0.8 }],
    ["rect", { x: 0.8, y: -5.4, width: 4.6, height: 4.6, rx: 0.8 }],
    ["rect", { x: -5.4, y: 0.8, width: 4.6, height: 4.6, rx: 0.8 }],
    ["rect", { x: 0.8, y: 0.8, width: 4.6, height: 4.6, rx: 0.8 }],
  ],
  Data: [
    ["ellipse", { cx: 0, cy: -4, rx: 5.2, ry: 2 }],
    ["path", { d: "M -5.2 -4 V 4 a 5.2 2 0 0 0 10.4 0 V -4" }],
    ["path", { d: "M -5.2 0 a 5.2 2 0 0 0 10.4 0" }],
  ],
  Service: [
    ["path", { d: "M -5.6 5.4 V -2.4 L 0 -5.6 L 5.6 -2.4 V 5.4 Z" }],
    ["path", { d: "M -2.2 5.4 V 1.4 H 2.2 V 5.4" }],
  ],
};

/** Below this radius a glyph is a smudge rather than a signal, so it is left out. */
const GLYPH_MIN_RADIUS = 10;

/** Accounts drawn per page of the accounts stack — the server's NamedIdentityCap. */
const ACCOUNT_PAGE = 12;

export function Glyph({ stage, colour, scale = 1 }) {
  const spec = GLYPHS[stage];
  if (!spec) return null;
  return (
    <g transform={`scale(${scale})`} stroke={colour} strokeWidth={1.4} fill="none"
       strokeLinecap="round" strokeLinejoin="round">
      {spec.map(([tag, attrs], i) => React.createElement(tag, { key: i, ...attrs }))}
    </g>
  );
}

/** 14px at no exposure, 36px at full — the eye lands on the worst thing before it reads a word. */
export const radiusFor = (weight) => 14 + (Math.max(0, Math.min(100, weight || 0)) / 100) * 22;

/** Deterministic spread, so a reload does not reshuffle the estate under the reader. */
const hash = (value) => {
  let h = 0;
  const s = String(value);
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

/**
 * Loose clusters under a stage band, deliberately NOT a grid: columns would imply an ordering
 * inside a stage that the data does not have. X jitters within the band, Y stacks with slack.
 */
export function layoutNodes(nodes) {
  const perStage = {};
  return nodes.map((n) => {
    const stage = STAGE_INDEX[n.stage] ?? STAGES.length;
    const seat = (perStage[stage] = (perStage[stage] || 0) + 1) - 1;
    const r = radiusFor(n.weight);
    const jitter = hash(`${n.stage}:${n.name}:${n.id}`);
    return {
      ...n,
      position: {
        x: stage * 250 + (jitter % 88) - 44 - r,
        y: seat * 92 + (Math.floor(jitter / 88) % 44) - 22 - r,
      },
    };
  });
}

/**
 * Everything on the chain THROUGH one object: what leads to it, and what it leads to.
 *
 * <p>Ancestors and descendants, never "any node within N hops". On a merged estate a hop-count
 * would walk backwards out of a shared node — focus an account, reach its Global Administrator
 * role, and every other holder of that role comes back with it, which is the crowding this exists
 * to remove. Walking strictly outward from the focus in each direction keeps the answer to "what
 * does THIS object touch".</p>
 *
 * <p>Edges are filtered to pairs that both survive: React Flow throws on an edge whose endpoint is
 * missing, so a filter that only touched nodes would break the canvas rather than hide things.</p>
 */
export function relatedSubgraph(graph, nodeId) {
  if (!graph?.nodes?.length || nodeId == null) return graph;
  const id = String(nodeId);
  if (!graph.nodes.some((n) => String(n.id) === id)) return graph;

  const forward = new Map();
  const backward = new Map();
  (graph.edges || []).forEach((e) => {
    const from = String(e.fromNodeId);
    const to = String(e.toNodeId);
    if (!forward.has(from)) forward.set(from, []);
    if (!backward.has(to)) backward.set(to, []);
    forward.get(from).push(to);
    backward.get(to).push(from);
  });

  const reach = (adjacency) => {
    const seen = new Set();
    const stack = [id];
    while (stack.length) {
      (adjacency.get(stack.pop()) || []).forEach((next) => {
        if (seen.has(next)) return;
        seen.add(next);
        stack.push(next);
      });
    }
    return seen;
  };

  const keep = new Set([id, ...reach(forward), ...reach(backward)]);
  return {
    ...graph,
    nodes: graph.nodes.filter((n) => keep.has(String(n.id))),
    edges: (graph.edges || []).filter(
      (e) => keep.has(String(e.fromNodeId)) && keep.has(String(e.toNodeId))
    ),
  };
}

function MapNode({ data }) {
  const t = tone(data.severity);
  const r = radiusFor(data.weight);
  const size = r * 2;
  const soft = data.status !== "observed";

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <Handle type="target" position={Position.Left} className="!opacity-0" />
      <div
        title={data.name}
        className={`flex h-full w-full items-center justify-center rounded-full ${
          data.isSelected ? "ring-2 ring-offset-1" : ""
        }`}
        style={{
          background: t.fill,
          border: `${data.isFocus ? 3 : 2.2}px ${soft ? "dashed" : "solid"} ${t.ring}`,
          // The selection ring uses the node's own colour so it stays visible on a red node
          // instead of being swallowed by the border.
          "--tw-ring-color": t.ring,
        }}
      >
        {data.isStack ? (
          <span className="text-[11px] font-bold leading-none" style={{ color: t.ring }}>
            {data.isOpen ? "−" : "+"}
          </span>
        ) : r >= GLYPH_MIN_RADIUS ? (
          <svg width={size * 0.6} height={size * 0.6} viewBox="-8 -8 16 16" aria-hidden="true">
            <Glyph stage={data.stage} colour={t.ring} />
          </svg>
        ) : null}
      </div>
      {/* The label sits outside the dot so it never shrinks with the radius. */}
      <div
        className="pointer-events-none absolute left-1/2 top-full mt-1 w-32 -translate-x-1/2 truncate text-center text-[10px] leading-tight text-content-secondary"
        title={data.name}
      >
        {data.name}
      </div>
      <Handle type="source" position={Position.Right} className="!opacity-0" />
    </div>
  );
}

const nodeTypes = { mapNode: MapNode };

const storageKey = (orgId, view) => `axaris.identityMap.${orgId}.${view}`;

/** Per-viewer layout only. A throwing or cleared store must never stop the map rendering. */
function loadPositions(key) {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function savePositions(key, positions) {
  try {
    window.localStorage.setItem(key, JSON.stringify(positions));
  } catch {
    /* a remembered layout is a convenience, not a requirement */
  }
}

function clearPositions(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* nothing to undo */
  }
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-2 py-2">
      {STAGES.map((s) => (
        <span key={s.key} className="flex items-center gap-1.5 text-[11px] text-content-secondary">
          <svg width="14" height="14" viewBox="-8 -8 16 16" aria-hidden="true">
            <Glyph stage={s.key} colour="currentColor" />
          </svg>
          {s.label}
        </span>
      ))}
      <span className="mx-1 h-3 w-px bg-border" />
      {Object.entries(SEVERITY).map(([key, v]) => (
        <span key={key} className="flex items-center gap-1.5 text-[11px] text-content-secondary">
          <span className="h-2.5 w-2.5 rounded-full border-2" style={{ borderColor: v.ring, background: v.fill }} />
          {v.label}
        </span>
      ))}
      <span className="text-[11px] text-content-muted">Bigger dot = more exposure · drag any dot · a + dot stands for a group not drawn one by one; click it to open it</span>
    </div>
  );
}

function Detail({ node, onOpenIdentity }) {
  if (!node) {
    return (
      <div className="p-3 text-xs text-content-secondary">
        Pick a dot to see what it is and why it is on the map.
      </div>
    );
  }
  const t = tone(node.severity);
  return (
    <div className="p-3">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 h-3 w-3 shrink-0 rounded-full border-2"
              style={{ borderColor: t.ring, background: t.fill }} />
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-content-primary">{node.name}</div>
          <div className="text-[11px] uppercase tracking-wide text-content-muted">
            {(STAGES.find((s) => s.key === node.stage) || {}).label || node.stage}
          </div>
        </div>
      </div>

      {node.detail && <p className="mt-2 text-xs leading-relaxed text-content-secondary">{node.detail}</p>}

      {/* The reason a stage is empty is the whole value of drawing it, so it is never truncated. */}
      {node.reason && (
        <p className="mt-2 rounded-md border border-border bg-theme-secondary px-2 py-1.5 text-xs leading-relaxed text-content-secondary">
          {node.reason}
        </p>
      )}

      {node.isStack && (
        <p className="mt-2 text-xs text-content-secondary">
          Stands for <span className="font-semibold text-content-primary">{node.childCount}</span>{" "}
          item(s) not drawn one by one, so the map stays readable. Click the dot to draw each one
          on its own; the bar above the map takes it back.
        </p>
      )}

      {node.identityId != null && onOpenIdentity && (
        <button type="button"
          onClick={() => onOpenIdentity(node.identityId)}
          className="mt-3 w-full rounded-lg border border-border px-2 py-1.5 text-xs font-semibold text-content-primary hover:bg-theme-secondary">
          Open this account →
        </button>
      )}
    </div>
  );
}

export default function IdentityMap({
  organizationId, identityId = null, onOpenIdentity, height = 560,
  /**
   * Stack the detail under the canvas instead of beside it. Tailwind's `lg:` is a VIEWPORT query,
   * not a container one, so inside a narrow drawer on a wide screen the side panel still claimed
   * 16rem and left the chain about 200px to draw in. The parent knows how much room it is really
   * giving, so it says.
   */
  compact = false,
}) {
  const apiCall = useApi();
  const view = identityId == null ? "estate" : `identity-${identityId}`;
  const key = storageKey(organizationId, view);

  const [graph, setGraph] = useState(null); // null = loading
  const [failed, setFailed] = useState(null);
  // True while a refetch is in flight. The old map stays drawn underneath, so without this a click
  // on a + dot looked like it did nothing — and every repeat click asked for 12 more accounts.
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState([]); // stage names drawn open
  const [namedLimit, setNamedLimit] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  // When set, only this object's chain is drawn. The estate view merges every account's lines into
  // one mass; isolating the clicked object is what makes a single chain readable.
  const [focusId, setFocusId] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [moved, setMoved] = useState(false);
  const flowRef = useRef(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (identityId != null) params.set("identityId", String(identityId));
    if (expanded.length) params.set("expand", expanded.join(","));
    if (namedLimit) params.set("namedLimit", String(namedLimit));
    return params.toString();
  }, [identityId, expanded, namedLimit]);

  useEffect(() => {
    let cancelled = false;
    setFailed(null);
    setRefreshing(true);
    apiCall("GET", `/defender-identity/map${query ? `?${query}` : ""}`)
      .then((r) => {
        if (cancelled) return;
        if (!r || !Array.isArray(r.nodes)) {
          setFailed("The map could not be built from this tenant's data.");
          return;
        }
        // Node ids are assigned per build, so a selection or focus held across a refetch would
        // point at whatever took that number.
        setSelectedId(null);
        setFocusId(null);
        setGraph(r);
      })
      .catch(() => { if (!cancelled) setFailed("The map could not be loaded."); })
      .finally(() => { if (!cancelled) setRefreshing(false); });
    return () => { cancelled = true; };
  }, [apiCall, query]);

  // The accounts stack is not drawn as a dot. On a real estate it is one small grey circle among
  // dozens, and the only way to the hundreds of accounts behind it; it lives in a labelled panel in
  // the canvas corner instead. The apps stack stays a dot — it sits in its own stage's column.
  const accountStack = useMemo(
    () => (graph?.nodes || []).find((n) => n.isStack && n.stage === "Identity") || null,
    [graph]
  );
  const drawable = useMemo(() => {
    if (!graph || !accountStack) return graph;
    const hidden = String(accountStack.id);
    return {
      ...graph,
      nodes: graph.nodes.filter((n) => String(n.id) !== hidden),
      edges: (graph.edges || []).filter((e) => String(e.fromNodeId) !== hidden && String(e.toNodeId) !== hidden),
    };
  }, [graph, accountStack]);

  const visible = useMemo(
    () => (focusId == null ? drawable : relatedSubgraph(drawable, focusId)),
    [drawable, focusId]
  );

  // Positions are state, not derived: once a node is dragged we must not recompute it. Keyed on
  // structure only, so selecting a dot never snaps the canvas back.
  const structureKey = useMemo(
    () => [
      (visible?.nodes || []).map((n) => `${n.id}:${n.stage}:${n.name}`).join("|"),
      expanded.join(","),
    ].join("#"),
    [visible, expanded]
  );

  const laidOut = useMemo(() => {
    if (!visible?.nodes?.length) return [];
    const stored = loadPositions(key);
    return layoutNodes(visible.nodes).map((n) => {
      // A remembered position is keyed by what the node IS, not by its id — ids are assigned per
      // build, so an id key would move a dragged dot onto whatever took its number.
      const saved = stored[`${n.stage}:${n.name}`];
      return {
        id: String(n.id),
        type: "mapNode",
        position: saved && Number.isFinite(saved.x) && Number.isFinite(saved.y) ? saved : n.position,
        data: {
          ...n,
          isFocus: String(n.id) === String(focusId ?? graph.focusNodeId),
          isSelected: false,
          isOpen: expanded.includes(n.stage),
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structureKey]);

  useEffect(() => {
    setNodes(laidOut);
    setMoved(false);
    if (laidOut.length) {
      const frame = requestAnimationFrame(() => flowRef.current?.fitView({ padding: 0.2 }));
      return () => cancelAnimationFrame(frame);
    }
    return undefined;
  }, [laidOut, setNodes]);

  // Restyle in place, so a drag survives a selection change.
  useEffect(() => {
    setNodes((current) =>
      current.map((n) => {
        const isSelected = n.id === String(selectedId);
        return n.data.isSelected === isSelected ? n : { ...n, data: { ...n.data, isSelected } };
      })
    );
  }, [selectedId, setNodes]);

  const edges = useMemo(() => {
    if (!visible?.nodes?.length) return [];
    const byId = new Map(visible.nodes.map((n) => [String(n.id), n]));
    return (visible.edges || []).map((e) => {
      const t = tone(e.severity);
      const from = byId.get(String(e.fromNodeId));
      const to = byId.get(String(e.toNodeId));
      const soft = from?.status !== "observed" || to?.status !== "observed";
      return {
        id: `${e.fromNodeId}-${e.toNodeId}-${e.edgeType}`,
        source: String(e.fromNodeId),
        target: String(e.toNodeId),
        type: "default",
        style: {
          stroke: t.ring,
          // More than one underlying fact asserted this link, so it is drawn heavier.
          strokeWidth: (e.sourceCount || 1) > 1 ? 2 : 1.2,
          strokeOpacity: e.severity === "grey" ? 0.45 : 0.75,
          strokeDasharray: soft ? "4 3" : undefined,
        },
        data: e,
      };
    });
  }, [visible]);

  const selected = useMemo(
    () => (visible?.nodes || []).find((n) => String(n.id) === String(selectedId)) || null,
    [visible, selectedId]
  );

  const focused = useMemo(
    () => (graph?.nodes || []).find((n) => String(n.id) === String(focusId)) || null,
    [graph, focusId]
  );

  const handleNodeClick = useCallback((_event, node) => {
    const found = (visible?.nodes || []).find((n) => String(n.id) === node.id);
    // One group change at a time: a second click while the first is loading would stack another
    // page of accounts on top of the one already coming.
    if (found?.isStack && refreshing) return;
    setSelectedId(node.id);
    if (found?.isStack) {
      // A stack opens instead of isolating. (The accounts stack is not on the canvas; its panel
      // in the corner pages it.)
      setExpanded((current) => (current.includes(found.stage)
        ? current.filter((s) => s !== found.stage)
        : [...current, found.stage]));
      return;
    }
    // Clicking the isolated object again puts the estate back, so focus is never a trap.
    setFocusId((current) => (String(current) === node.id ? null : node.id));
  }, [visible, refreshing]);

  const appsOpen = expanded.includes("App");
  const extraAccounts = namedLimit ? namedLimit - ACCOUNT_PAGE : 0;
  const drawnAccounts = (graph?.nodes || []).filter((n) => n.stage === "Identity" && !n.isStack).length;
  const hiddenAccounts = accountStack?.childCount || 0;
  // A page at a time — opening the stack fully would put the whole estate back on the canvas,
  // which is what the stack exists to avoid. Ignored while a page is still loading.
  const moreAccounts = useCallback(() => {
    if (refreshing) return;
    setNamedLimit((current) => (current || ACCOUNT_PAGE) + ACCOUNT_PAGE);
  }, [refreshing]);
  // Back down by the same step; the first page is the server's default, so it is sent as no limit.
  const fewerAccounts = useCallback(() => {
    setNamedLimit((current) => (current && current - ACCOUNT_PAGE > ACCOUNT_PAGE ? current - ACCOUNT_PAGE : null));
  }, []);
  const closeApps = useCallback(() => {
    setExpanded((current) => current.filter((s) => s !== "App"));
  }, []);

  const persist = useCallback(() => {
    const positions = {};
    nodes.forEach((n) => { positions[`${n.data.stage}:${n.data.name}`] = n.position; });
    savePositions(key, positions);
    setMoved(true);
  }, [nodes, key]);

  const relayout = useCallback(() => {
    clearPositions(key);
    // Keyed by id, not by index: the two lists happen to be in the same order today and a reorder
    // would otherwise silently move every dot onto a different node's seat.
    const fresh = new Map(layoutNodes(drawable?.nodes || []).map((n) => [String(n.id), n.position]));
    setNodes((current) => current.map((n) => ({ ...n, position: fresh.get(n.id) || n.position })));
    setMoved(false);
    flowRef.current?.fitView({ padding: 0.2, duration: 200 });
  }, [drawable, setNodes, key]);

  if (failed) {
    return (
      <div className="rounded-lg border border-border bg-theme-card-solid p-4 text-sm text-content-secondary"
           data-testid="identity-map-failed">
        {failed}
      </div>
    );
  }

  if (graph === null) {
    return (
      <div className="rounded-lg border border-border bg-theme-card-solid p-4 text-xs text-content-muted"
           data-testid="identity-map-loading">
        Drawing the identity map…
      </div>
    );
  }

  if (!graph.nodes.length) {
    return (
      <div className="rounded-lg border border-border bg-theme-card-solid p-4 text-sm text-content-secondary"
           data-testid="identity-map-empty">
        No identities have been synced yet, so there is no chain to draw.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-theme-card-solid" data-testid="identity-map">
      {graph.truncated && (
        <div className="border-b border-border bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Showing the {graph.nodes.length} most exposed of {graph.totalNodeCount} nodes. This is a
          partial view — open a group or narrow to one account to see the rest.
        </div>
      )}

      {/* Isolating must always announce itself: a filtered canvas that looked like the whole estate
          would be the same mistake as a truncated one reading as complete. */}
      {focused && (
        <div className="flex items-center justify-between gap-3 border-b border-border bg-indigo-50 px-3 py-2 text-xs text-indigo-900"
             data-testid="identity-map-focus">
          <span className="min-w-0 truncate">
            Showing only <span className="font-semibold">{focused.name}</span> and what it connects
            to — {visible.nodes.length} of {graph.nodes.length} dots.
          </span>
          <button type="button" onClick={() => setFocusId(null)}
            className="shrink-0 font-semibold underline hover:no-underline">
            Show everything
          </button>
        </div>
      )}

      {/* An opened group is drawn as its members and the + dot leaves the canvas, so the way back
          has to live off the canvas: without this bar an opened group could only be closed by
          reloading the page. */}
      {appsOpen && (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border bg-theme-secondary px-3 py-2 text-xs text-content-secondary"
             data-testid="identity-map-opened">
          <span className="min-w-0">
            Opened: tenant-wide apps, one dot each
          </span>
          <button type="button" onClick={closeApps} disabled={refreshing}
            className="shrink-0 font-semibold text-content-primary underline hover:no-underline disabled:opacity-40 disabled:no-underline">
            Group tenant-wide apps again
          </button>
        </div>
      )}

      <div className={`flex flex-col ${compact ? "" : "lg:flex-row"}`}>
        {/* `flex-1` is `flex: 1 1 0%`, which in a COLUMN container applies to the HEIGHT axis and
            beats the inline height — the parent is content-sized, so there is no free space to grow
            into and the canvas collapses to 0px. React Flow then measures an empty box, renders
            nothing, and its ResizeObserver thrashes ("loop completed with undelivered
            notifications"). It belongs only on the row layout, where it governs width — and the
            non-compact layout is a column below `lg` (laptops under 1024px, tablets, phones), so
            there it must be `lg:` only too or the map vanishes on small screens. */}
        <div className={`relative min-w-0 w-full shrink-0 ${compact ? "" : "lg:w-auto lg:flex-1"}`} style={{ height }}
             data-testid="identity-map-canvas">
          {/* Fixed in the corner, off the graph: the way to the accounts that are not drawn. Under
              the loader (z-10), so it cannot be clicked while a page is coming. */}
          {(accountStack || extraAccounts > 0) && (
            <div className="absolute left-2 top-2 z-[5] rounded-lg border border-border bg-theme-card-solid px-3 py-2 shadow-sm"
                 data-testid="identity-map-accounts">
              <div className="text-xs font-semibold text-content-primary">
                {hiddenAccounts > 0 ? `${hiddenAccounts} more accounts not drawn` : "All accounts drawn"}
              </div>
              <div className="text-[11px] text-content-muted">
                Showing {drawnAccounts} of {drawnAccounts + hiddenAccounts}, most exposed first
              </div>
              <div className="mt-1.5 flex items-center gap-1.5">
                {hiddenAccounts > 0 && (
                  <button type="button" onClick={moreAccounts} disabled={refreshing}
                    className="rounded-md bg-indigo-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-indigo-700 disabled:opacity-40">
                    + Show {Math.min(ACCOUNT_PAGE, hiddenAccounts)} more
                  </button>
                )}
                {extraAccounts > 0 && (
                  <button type="button" onClick={fewerAccounts} disabled={refreshing}
                    className="rounded-md border border-border px-2 py-1 text-[11px] font-semibold text-content-primary hover:bg-theme-secondary disabled:opacity-40">
                    − Show {Math.min(ACCOUNT_PAGE, extraAccounts)} fewer
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Covers the canvas while it reloads, so the stale map cannot be clicked again. */}
          {refreshing && (
            <div className="absolute inset-0 z-10 flex items-center justify-center"
                 data-testid="identity-map-refreshing" role="status">
              {/* The theme colours are CSS variables, which Tailwind's /70 shorthand cannot fade. */}
              <div className="absolute inset-0 bg-theme-card-solid opacity-70" />
              <span className="relative flex items-center gap-2 rounded-md border border-border bg-theme-card-solid px-3 py-2 text-xs text-content-secondary shadow-sm">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Redrawing the map…
              </span>
            </div>
          )}
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onNodeClick={handleNodeClick}
            onNodeDragStop={persist}
            onInit={(instance) => { flowRef.current = instance; }}
            nodesConnectable={false}
            fitView
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={18} color="var(--border-color)" />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>

        <div className={`w-full shrink-0 border-t border-border ${compact ? "" : "lg:w-64 lg:border-l lg:border-t-0"}`}>
          <Detail node={selected} onOpenIdentity={onOpenIdentity} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-border">
        <Legend />
        <button type="button"
          onClick={relayout}
          disabled={!moved}
          className="mr-2 shrink-0 text-[11px] text-content-secondary hover:text-content-primary disabled:opacity-40 disabled:hover:text-content-secondary"
          title="Restore the automatic layout">
          Re-layout
        </button>
      </div>
    </div>
  );
}
